/**
 * Broker Configuration System
 * 
 * This file defines the configuration for each broker platform.
 * 
 * HOW TO ADD A NEW BROKER:
 * ========================
 * 
 * 1. Define the fields for your broker by creating a BrokerFieldConfig array:
 *    const NEW_BROKER_FIELDS: BrokerFieldConfig[] = [
 *      {
 *        key: 'username',
 *        label: 'Username',
 *        type: 'text',
 *        required: true,
 *        placeholder: 'Enter username',
 *      },
 *      // Add more fields as needed...
 *    ];
 * 
 * 2. Add a new entry to the BROKERS object:
 *    export const BROKERS: Record<string, BrokerConfig> = {
 *      // ... existing brokers ...
 *      newbroker: {
 *        id: 'newbroker',
 *        name: 'newbroker',
 *        displayName: 'New Broker',
 *        description: 'Connect your New Broker account',
 *        fields: NEW_BROKER_FIELDS,
 *        transformData: (formData) => ({
 *          username: formData.username.trim(),
 *          broker_name: 'newbroker',
 *          password: formData.password,
 *          // Add any broker-specific fields here
 *        }),
 *      },
 *    };
 * 
 * 3. That's it! The form will automatically render based on your field configuration.
 * 
 * FIELD TYPES:
 * ============
 * - 'text': Standard text input
 * - 'password': Password input (masked)
 * - 'number': Numeric input
 * - 'tel': Telephone input (with digit-only validation)
 * - 'email': Email input (with email validation)
 * 
 * FIELD OPTIONS:
 * ==============
 * - key: Unique identifier for the field (used as form data key)
 * - label: Display label for the field
 * - type: Input type (see FIELD TYPES above)
 * - required: Whether the field is mandatory
 * - placeholder: Placeholder text
 * - helpText: Helper text shown below the field
 * - maxLength: Maximum character length
 * - minLength: Minimum character length
 * - pattern: Regex pattern for validation
 * - prefix: Prefix shown before input (e.g., '+91' for phone numbers)
 * - transform: Function to transform value before sending to API
 * - validate: Custom validation function (returns error message or null)
 * 
 * DATA TRANSFORMATION:
 * ====================
 * The transformData function allows you to customize how form data is sent to the API.
 * This is useful for:
 * - Adding broker_name automatically
 * - Formatting values (e.g., prepending country code to phone numbers)
 * - Filtering out empty optional fields
 * - Renaming fields if needed
 */

export type FieldType = 'text' | 'password' | 'number' | 'tel' | 'email';

export interface BrokerFieldConfig {
  key: string;
  label: string;
  type: FieldType;
  required: boolean;
  placeholder?: string;
  helpText?: string;
  maxLength?: number;
  minLength?: number;
  pattern?: string;
  prefix?: string; // For fields like mobile number (+91)
  transform?: (value: string) => string; // Transform value before sending to API
  validate?: (value: string) => string | null; // Custom validation function
}

export interface BrokerConfig {
  id: string;
  name: string;
  displayName: string;
  description: string;
  fields: BrokerFieldConfig[];
  transformData?: (formData: Record<string, string>) => Record<string, unknown>; // Transform entire form data
}

/**
 * Field configurations for each broker
 */
const ZERODHA_FIELDS: BrokerFieldConfig[] = [
  {
    key: 'api_key',
    label: 'API Key',
    type: 'text',
    required: true,
    placeholder: 'Enter your Zerodha API key',
    helpText: 'Your Zerodha Kite Connect API key',
  },
  {
    key: 'api_secret',
    label: 'API Secret',
    type: 'password',
    required: true,
    placeholder: 'Enter your Zerodha API secret',
    helpText: 'Your Zerodha Kite Connect API secret',
  },
];

const KOTAK_FIELDS: BrokerFieldConfig[] = [
  {
    key: 'ucc',
    label: 'UCC',
    type: 'text',
    required: true,
    placeholder: 'Enter UCC',
    helpText: '5 characters',
    maxLength: 5,
    minLength: 5,
    validate: (value: string) => {
      if (value.length !== 5) {
        return 'UCC must be exactly 5 characters';
      }
      return null;
    },
  },
  {
    key: 'mobile',
    label: 'Mobile Number',
    type: 'tel',
    required: true,
    placeholder: '1234567890',
    helpText: '10 digits',
    prefix: '+91',
    maxLength: 10,
    minLength: 10,
    transform: (value: string) => `+91${value}`,
    validate: (value: string) => {
      if (value.length !== 10) {
        return 'Mobile number must be exactly 10 digits';
      }
      if (!/^\d+$/.test(value)) {
        return 'Mobile number must contain only digits';
      }
      return null;
    },
  },
  {
    key: 'mpin',
    label: 'MPIN',
    type: 'password',
    required: true,
    placeholder: 'Enter MPIN',
    helpText: '6 digits',
    maxLength: 6,
    minLength: 6,
    validate: (value: string) => {
      if (value.length !== 6) {
        return 'MPIN must be exactly 6 digits';
      }
      if (!/^\d+$/.test(value)) {
        return 'MPIN must contain only digits';
      }
      return null;
    },
  },
  {
    key: 'application_token',
    label: 'Application Token',
    type: 'text',
    required: true,
    placeholder: 'Enter application token',
  },
];

/**
 * Bajaj broker has no fields - just needs broker_name to create
 */
const BAJAJ_FIELDS: BrokerFieldConfig[] = [];

/**
 * IndMoney broker has no fields - just needs broker_name to create
 * Token is submitted manually via submit-token endpoint
 */
const INDMONEY_FIELDS: BrokerFieldConfig[] = [];

/**
 * Groww broker has no fields - just needs broker_name to create
 * Token is submitted manually via submit-token endpoint
 */
const GROWW_FIELDS: BrokerFieldConfig[] = [];

/**
 * Dhan broker has no fields - just needs broker_name to create
 * Token is submitted manually via submit-token endpoint
 */
const DHAN_FIELDS: BrokerFieldConfig[] = [];

/**
 * MasterTrust broker fields
 */
const MASTERTRUST_FIELDS: BrokerFieldConfig[] = [
  {
    key: 'client_id',
    label: 'Client ID',
    type: 'text',
    required: true,
    placeholder: 'Enter your MasterTrust Client ID',
    helpText: 'Your MasterTrust API Client ID',
  },
  {
    key: 'client_secret',
    label: 'Client Secret',
    type: 'password',
    required: true,
    placeholder: 'Enter your MasterTrust Client Secret',
    helpText: 'Your MasterTrust API Client Secret',
  },
];

/**
 * Axis Direct broker fields
 * Axis issues these after onboarding you as a RAPID API client
 */
const AXIS_FIELDS: BrokerFieldConfig[] = [
  {
    key: 'client_id',
    label: 'Client ID',
    type: 'text',
    required: true,
    placeholder: 'RAPID_XXXXXXX',
    helpText: 'The x-api-client-id Axis Securities emails you after RAPID API onboarding',
  },
  {
    key: 'authorization_key',
    label: 'Authorization Key',
    type: 'password',
    required: true,
    placeholder: 'Enter your Axis authorization key',
    helpText: 'The Authorization key sent with your client ID. After adding, log in with Axis Direct SSO on the next page.',
  },
];

/**
 * Broker configurations
 */
export const BROKERS: Record<string, BrokerConfig> = {
  zerodha: {
    id: 'zerodha',
    name: 'zerodha',
    displayName: 'Zerodha',
    description: 'Connect your Zerodha account using API credentials',
    fields: ZERODHA_FIELDS,
    transformData: (formData) => ({
      broker_name: 'zerodha',
      api_key: formData.api_key.trim(),
      api_secret: formData.api_secret,
    }),
  },
  kotak: {
    id: 'kotak',
    name: 'kotak',
    displayName: 'Kotak',
    description: 'Connect your Kotak account',
    fields: KOTAK_FIELDS,
    transformData: (formData) => ({
      broker_name: 'kotak',
      ucc: formData.ucc,
      mobile: formData.mobile ? `+91${formData.mobile}` : undefined,
      mpin: formData.mpin,
      application_token: formData.application_token.trim(),
    }),
  },
  bajaj: {
    id: 'bajaj',
    name: 'bajaj',
    displayName: 'Bajaj',
    description: 'Connect your Bajaj Broking account',
    fields: BAJAJ_FIELDS,
    transformData: () => ({
      broker_name: 'bajaj',
    }),
  },
  mastertrust: {
    id: 'mastertrust',
    name: 'mastertrust',
    displayName: 'MasterTrust',
    description: 'Connect your MasterTrust account using API credentials',
    fields: MASTERTRUST_FIELDS,
    transformData: (formData) => ({
      broker_name: 'mastertrust',
      client_id: formData.client_id.trim(),
      client_secret: formData.client_secret,
    }),
  },
  indmoney: {
    id: 'indmoney',
    name: 'indmoney',
    displayName: 'IndMoney',
    description: 'Connect your IndMoney account',
    fields: INDMONEY_FIELDS,
    transformData: () => ({
      broker_name: 'indmoney',
    }),
  },
  groww: {
    id: 'groww',
    name: 'groww',
    displayName: 'Groww',
    description: 'Connect your Groww account',
    fields: GROWW_FIELDS,
    transformData: () => ({
      broker_name: 'groww',
    }),
  },
  dhan: {
    id: 'dhan',
    name: 'dhan',
    displayName: 'Dhan',
    description: 'Connect your Dhan account',
    fields: DHAN_FIELDS,
    transformData: () => ({
      broker_name: 'dhan',
    }),
  },
  axis: {
    id: 'axis',
    name: 'axis',
    displayName: 'Axis Direct',
    description: 'Add your RAPID API keys, then log in with Axis Direct SSO',
    fields: AXIS_FIELDS,
    transformData: (formData) => ({
      broker_name: 'axis',
      client_id: formData.client_id.trim(),
      authorization_key: formData.authorization_key,
    }),
  },
};

/**
 * Get all available brokers
 */
export function getAvailableBrokers(): BrokerConfig[] {
  return Object.values(BROKERS);
}

